import React from 'react';
import { Check, X } from 'lucide-react';
import { ANSWER_SHAPES } from '../../brand';
import { AnswerShapeIcon } from './AnswerShapeIcon';

interface Option {
  id: string;
  text: string;
}

interface Props {
  options: Option[];
  selectedOptionId?: string;
  onSelect: (optionId: string) => void;
  disabled?: boolean;
  /** Tras el cierre de la pregunta: ids correctos que llegaron del servidor. */
  correctOptionIds?: string[];
  /** Cuántos eligieron cada opción. Solo se muestra en la vista del anfitrión. */
  distribution?: Record<string, number>;
  totalAnswers?: number;
}

export const MultipleChoiceQuestion: React.FC<Props> = ({
  options,
  selectedOptionId,
  onSelect,
  disabled = false,
  correctOptionIds,
  distribution,
  totalAnswers = 0,
}) => {
  const revealed = Array.isArray(correctOptionIds);

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4 w-full max-w-5xl mx-auto">
      {options.map((opt, index) => {
        const shape = ANSWER_SHAPES[index % ANSWER_SHAPES.length];
        const isSelected = selectedOptionId === opt.id;
        const isCorrect = revealed && correctOptionIds!.includes(opt.id);
        const isWrongPick = revealed && isSelected && !isCorrect;

        // Antes del cierre todas las opciones se ven igual de válidas.
        // Después, las descartadas se atenúan en vez de desaparecer: el
        // jugador necesita seguir leyendo la que eligió.
        // Sobre el gris de las descartadas el texto blanco desaparece en el
        // tema claro, así que ahí el texto pasa al color de tinta.
        const dimmed = revealed && !isCorrect;
        let tone = shape.button;
        if (revealed) {
          tone = isCorrect
            ? 'bg-success-500 border-success-400'
            : isWrongPick
              ? 'bg-danger-500/10 border-danger-500'
              : 'bg-ink-850 border-ink-700 opacity-70';
        }

        const share =
          distribution && totalAnswers > 0
            ? Math.round(((distribution[opt.id] ?? 0) / totalAnswers) * 100)
            : null;

        return (
          <button
            key={opt.id}
            type="button"
            onClick={() => !disabled && onSelect(opt.id)}
            disabled={disabled}
            aria-pressed={isSelected}
            className={`
              relative overflow-hidden group flex items-center gap-4 w-full
              px-4 sm:px-5 py-4 sm:py-5 md:py-7 min-h-16 rounded-2xl border-2 text-left
              transition-all duration-200 ease-out
              ${tone}
              ${isSelected && !revealed ? `ring-4 ring-offset-2 ring-offset-ink-950 ${shape.ring} scale-[1.02]` : ''}
              ${disabled ? 'cursor-default' : 'cursor-pointer hover:scale-[1.02] active:scale-[0.99]'}
            `}
          >
            {/* Barra de reparto de respuestas, detrás del texto. */}
            {share !== null && (
              <span
                className="absolute inset-y-0 left-0 bg-white/12 transition-[width] duration-700 ease-out"
                style={{ width: `${share}%` }}
                aria-hidden="true"
              />
            )}

            <span className={`relative shrink-0 ${dimmed ? 'text-ink-500' : 'text-white/95'}`}>
              <AnswerShapeIcon shape={shape.id} className="w-8 h-8 md:w-9 md:h-9" />
            </span>

            <span
              className={`relative flex-1 font-semibold text-base md:text-xl leading-snug ${
                dimmed ? 'text-ink-200' : 'text-white'
              }`}
            >
              {opt.text}
            </span>

            {/* La forma se nombra para lectores de pantalla: el color no basta. */}
            <span className="sr-only">Opción {shape.label}</span>

            {share !== null && (
              <span
                className={`relative text-sm font-mono tabular-nums ${dimmed ? 'text-ink-400' : 'text-white/80'}`}
              >
                {share}%
              </span>
            )}

            {revealed && (
              <span className="relative shrink-0">
                {isCorrect ? (
                  <Check className="w-6 h-6 text-white" aria-label="Respuesta correcta" />
                ) : isWrongPick ? (
                  <X className="w-6 h-6 text-danger-400" aria-label="Tu respuesta, incorrecta" />
                ) : null}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
};
